import * as React from "react"
import { Sparkles } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { postJson } from "@/lib/admin-fetch"
import { inlinePatch } from "@/lib/inline-save"
import type { Lang } from "./types"

export type PageMetadataProps = {
  title_en: string | null
  title_es: string | null
  meta_description_en: string | null
  meta_description_es: string | null
  es_stale: boolean
}

function CharCount({ value, ideal }: { value: string; ideal: string }) {
  return (
    <>
      {value.length} characters — aim for {ideal}
    </>
  )
}

// Search metadata for one page: the <title> and meta description crawlers and
// social previews see. Same language split + AI assist as the sections, and
// the same per-field tick.
export function PageMetadataEditor({
  pageKey,
  metadata,
  lang,
  aiEnabled,
}: {
  pageKey: string
  metadata: PageMetadataProps
  lang: Lang
  aiEnabled: boolean
}) {
  const [drafting, setDrafting] = React.useState(false)
  const [aiError, setAiError] = React.useState<string | null>(null)

  const record = {
    title_en: metadata.title_en ?? "",
    title_es: metadata.title_es ?? "",
    meta_description_en: metadata.meta_description_en ?? "",
    meta_description_es: metadata.meta_description_es ?? "",
  }

  // The row holds all four columns, so one field's tick sends the other three
  // as the server already has them.
  const fields = useInlineRecord({
    record,
    save: (key, value) =>
      inlinePatch(`/admin/website/pages/${encodeURIComponent(pageKey)}/metadata`, {
        page_metadata: { ...record, [key]: value },
      }),
  })

  async function draftWithAi() {
    setDrafting(true)
    setAiError(null)
    try {
      const result = await postJson<{ title: string; description: string }>(
        "/admin/ai/draft-metadata",
        {
          page_key: pageKey,
          title: fields.value("title_en"),
          description: fields.value("meta_description_en"),
        },
      )
      fields.patch({ title_en: result.title, meta_description_en: result.description })
    } catch (e) {
      setAiError(e instanceof Error ? e.message : "Drafting failed.")
    } finally {
      setDrafting(false)
    }
  }

  const titleKey = lang === "en" ? "title_en" : "title_es"
  const descKey = lang === "en" ? "meta_description_en" : "meta_description_es"
  const titleState = fields.state(titleKey)
  const descState = fields.state(descKey)

  return (
    <div className="space-y-6">
      <p className="text-sm text-ink-muted">
        The page title and description shown in search results, browser tabs, and link previews.
        Leave blank to keep the page&apos;s built-in defaults.
      </p>

      {lang === "es" && (
        <div className="flex flex-wrap items-center gap-3">
          {metadata.es_stale ? (
            <Badge tone="signal">Spanish out of date</Badge>
          ) : (
            <Badge tone="muted">Spanish up to date</Badge>
          )}
        </div>
      )}

      <InlineField
        label={`Page title ${lang === "en" ? "(English)" : "(Spanish)"}`}
        htmlFor={`meta-title-${lang}`}
        state={titleState}
        hint={<CharCount value={titleState.value} ideal="under 60" />}
        above={
          lang === "es" && fields.value("title_en") !== "" ? (
            <p className="text-sm text-ink-muted">{fields.value("title_en")}</p>
          ) : undefined
        }
      >
        <Input
          id={`meta-title-${lang}`}
          value={titleState.value}
          onChange={(e) => fields.set(titleKey, e.target.value)}
          onKeyDown={titleState.keys}
        />
      </InlineField>

      <InlineField
        label={`Meta description ${lang === "en" ? "(English)" : "(Spanish)"}`}
        htmlFor={`meta-desc-${lang}`}
        state={descState}
        hint={<CharCount value={descState.value} ideal="120–160" />}
        above={
          lang === "es" && fields.value("meta_description_en") !== "" ? (
            <p className="text-sm text-ink-muted">{fields.value("meta_description_en")}</p>
          ) : undefined
        }
      >
        {/* A description is 120–160 characters: on a phone that is five lines
            of a box that showed one, with the rest scrolled off to the right.
            A textarea, as on the condition and therapy screens. */}
        <Textarea
          id={`meta-desc-${lang}`}
          value={descState.value}
          onChange={(e) => fields.set(descKey, e.target.value)}
          onKeyDown={descState.multilineKeys}
          rows={4}
        />
      </InlineField>

      {aiError && <p className="text-sm text-danger-display">{aiError}</p>}

      {lang === "en" && aiEnabled && (
        <Button type="button" size="sm" variant="secondary" disabled={drafting} onClick={draftWithAi}>
          <Sparkles className="mr-1.5 h-3.5 w-3.5" />
          {drafting ? "Drafting…" : "Draft from page content"}
        </Button>
      )}
    </div>
  )
}
